import { useEffect } from "react"
import { get } from '@util/xhrAPI'
import { useSelector, useDispatch } from 'react-redux'
import { fullUpdate } from '@store/slice/dashboardSlice'
import { Chart } from 'react-google-charts'
import { useTranslation } from 'react-i18next'
import useRandomHexaColorGenerator from '@hook/useRandomHexaColorGenerator'
import Div from '@ui/layout/Div'

const Dashboard = () => {
  const { mission } = useSelector(state => state.main)
  const { missionSubjects } = mission
  const missionSubjectConfig = missionSubjects[0]?.config
  const { data, displayedSeason } = useSelector(state => state.dashboard)
  const [t] = useTranslation('common')
  const dispatch = useDispatch()
  const getColor = useRandomHexaColorGenerator()

  const renderCallsTypePieChart = () => {
    const callsTypes = data['callsTypes'][displayedSeason]
    if ('undefined' === typeof callsTypes || 1 === Object.keys(callsTypes).length) {
      return <></>
    }

    const stats = [
      ["Calls Type", "Total calls"]
    ]

    for (let prop in callsTypes) {
      stats.push([t(`labels.${prop}_sterilization_call`), callsTypes[prop]])
    }

    const options = {
      title: t('titles.chart_calls_by_type'),
    };
    return (
      <Chart
        chartType="PieChart"
        data={stats}
        options={options}
        width={"100%"}
        height={"400px"}
      />
    );
  }

  const renderMissionSubjectsPieChart = () => {
    const missionSubjectsPartitions = data['missionSubjectsPartitions'][displayedSeason]
    if ('undefined' === typeof missionSubjectsPartitions || 1 === Object.keys(mission.missionSubjects).length) {
      return <></>
    }

    const stats = [
      ["Calls Type", "Total calls"]
    ]

    for (let prop in missionSubjectsPartitions) {
      stats.push([prop, missionSubjectsPartitions[prop]])
    }

    const options = {
      title: t('titles.chart_mission_subjects'),
    };
    return (
      <Chart
        chartType="PieChart"
        data={stats}
        options={options}
        width={"100%"}
        height={"400px"}
      />
    )
  }

  const renderMonthlyCallsEvolutionLineChart = () => {
    const monthlyEvolution = data['monthlyEvolution'][displayedSeason]
    if ('undefined' === typeof monthlyEvolution) {
      return <></>
    }

    let statsMonthlyEvolution = [
      [t('labels.month'), t('labels.calls')]
    ]
    const months = Object.keys(monthlyEvolution)

    const monthlyEvolutionData = months.map(month => {
      return [t(`months.${month}`), monthlyEvolution[month] ?? 0]
    })

    statsMonthlyEvolution = [...statsMonthlyEvolution, ...monthlyEvolutionData]

    const optionsMonthlyEvolution = {
      title: t('titles.monthly_line_calls_evolution'),
      curveType: "function",
      legend: { position: "bottom" },
    };
    return (
      <Chart
        chartType="LineChart"
        data={statsMonthlyEvolution}
        options={optionsMonthlyEvolution}
        width={"100%"}
        height={"400px"}
        legendToggle
      />
    );
  }

  const renderWeeklyCallsEvolutionLineChart = () => {
    const weeklyEvolution = data['weeklyEvolution'][displayedSeason]
    if ('undefined' === typeof weeklyEvolution) {
      return <></>
    }

    let statsWeeklyEvolution = [
      [t('labels.week'), t('labels.calls')]
    ]
    const weeks = Object.keys(weeklyEvolution)

    const weeklyEvolutionData = weeks.map(evolution => {
      const currentVal = evolution.split('/')
      const week = parseInt(currentVal[0])
      const month = currentVal[1]
      return [`${week}${1 === week ? t('labels.first_number_prefix') : t('labels.other_numbers_prefix')} ${t('labels.week')}  \n ${t(`months.${month}`)}`, weeklyEvolution[evolution] ?? 0]
    })

    statsWeeklyEvolution = [...statsWeeklyEvolution, ...weeklyEvolutionData]

    const optionsMonthlyEvolution = {
      title: t('titles.weekly_line_calls_evolution'),
      curveType: "function",
      legend: { position: "bottom" },
    };
    return (
      <Chart
        chartType="LineChart"
        data={statsWeeklyEvolution}
        options={optionsMonthlyEvolution}
        width={"100%"}
        height={"400px"}
        legendToggle
      />
    );
  }

  const renderCallsByDistrictBarChart = () => {
    const districtsPartitions = data['districtsPartitions'][displayedSeason]
    if ('undefined' === typeof districtsPartitions) {
      return <></>
    }

    let stats = [[t(`labels.districts`), t(`labels.calls`), { role: "style" }]]

    for (let prop in districtsPartitions) {
      stats.push([prop || t('labels.none'), districtsPartitions[prop], getColor()])
    }

    const options = {
      title: t(`titles.bubble_by_districts_partition`),
      chartArea: { width: "50%" },
      hAxis: {
        title: t(`labels.districts`),
        minValue: 0,
      },
      vAxis: {
        title: t(`labels.calls`),
      },
    };

    return (
      <Chart
        chartType="ColumnChart"
        data={stats}
        options={options}
        width={"100%"}
        height={"400px"}
      />
    );
  }

  const renderScanTreatmentTable = () => {
    const treatmentData = data['treatmentData'][displayedSeason]
    if ('undefined' === typeof treatmentData) {
      return <></>
    }

    const getScanData = () => {
      const scanRows = Object.keys(treatmentData).map(key => {
        return (
          <tr key={`${key}-scan-data`}>
            <td>{t(`table.${key}`)}</td>
            {
              missionSubjectConfig.withEggs
                ? <>
                  <td>{treatmentData[key]['totalNests']}</td>
                  <td>{treatmentData[key]['totalTreatedNests']}</td>
                  <td>{treatmentData[key]['totalNonTreatedNests']}</td>
                  <td>{treatmentData[key]['totalEmptyNests']}</td>
                  <td>{treatmentData[key]['totalBirds']}</td>
                  <td>{treatmentData[key]['totalEggs']}</td>
                  <td>{treatmentData[key]['totalTreatedEggs']}</td>
                  <td>{treatmentData[key]['totalNonTreatedEggs']}</td>
                  <td>{'undefined' !== treatmentData[key]['failedSterilizationEggs'] ? treatmentData[key]['failedSterilizationEggs'] : 0}</td>
                </>
                : <>
                  <td>{treatmentData[key]['totalNests']}</td>
                  <td>{treatmentData[key]['totalTreatedNests']}</td>
                  <td>{treatmentData[key]['totalNonTreatedNests']}</td>
                </>
            }
          </tr>
        )
      })
      return scanRows
    }

    return (
      <Div cssClass='column is-12'>
        <h3>{t('table.scans_resume')}</h3>
        <table className='table is-fullwidth is-hoverable is-bordered'>
          <thead>
            <tr>
              <th>{t('table.scan')}</th>
              {
                missionSubjectConfig.withEggs
                  ? <>
                    <th>{t('table.total_nests')}</th>
                    <th>{t('table.treated_nests')}</th>
                    <th>{t('table.nb_non_treated_nests')}</th>
                    <th>{t('table.nb_empty_nests')}</th>
                    <th>{t('table.nb_birds')}</th>
                    <th>{t('table.total_eggs')}</th>
                    <th>{t('table.nb_treated_eggs')}</th>
                    <th>{t('table.nb_non_treated_eggs')}</th>
                    <th>{t('table.nb_failed_eggs_sterilization')}</th>
                  </>
                  : <>
                    <th>{t('table.found')}</th>
                    <th>{t('table.nb_treated')}</th>
                    <th>{t('table.nb_non_treated')}</th>
                  </>
              }
            </tr>
          </thead>
          <tbody>
            {getScanData()}
          </tbody>
        </table>
      </Div>
    )
  }

  const renderScanDataByMissionSubject = () => {
    let treatmentMissionSubjectsData = data['treatmentMissionSubjectsData'][displayedSeason]

    if ('undefined' === typeof treatmentMissionSubjectsData || 1 === Object.keys(mission.missionSubjects).length) {
      return <></>
    }

    const keys = ['firstScan', 'secondScan']

    const getScanData = key => {
      return Object.keys(treatmentMissionSubjectsData[key]).map(subject => {
        if ('globalVue' !== subject) {
          return (
            <tr key={`${key}-${subject}`}>
              <td>{'Non specified' === subject ? t('labels.non_specified') : subject}</td>
              <td>{treatmentMissionSubjectsData[key][subject]['nests']}</td>
              <td>{treatmentMissionSubjectsData[key][subject]['nonTreatedNests']}</td>
              <td>{treatmentMissionSubjectsData[key][subject]['eggs']}</td>
              <td>{treatmentMissionSubjectsData[key][subject]['nonTreatedEggs']}</td>
              <td>{treatmentMissionSubjectsData[key][subject]['birds']}</td>
            </tr>
          )
        }
      })
    }

    return keys.map(key => {
      return (
        'undefined' !== typeof treatmentMissionSubjectsData[key] && Object.keys(treatmentMissionSubjectsData[key]).length > 0
        && <Div cssClass='column is-12' key={`mission-subject-${key}`}>
          <h3>{t('firstScan' === key ? 'table.first_scan' : 'table.second_scan')}</h3>
          <table className='table is-fullwidth is-hoverable'>
            <thead>
              <tr>
                <th>{t('table.mission_subjects')}</th>
                <th>{t('table.found_nests')}</th>
                <th>{t('table.nb_non_treated_nests')}</th>
                <th>{t('table.nb_treated_eggs')}</th>
                <th>{t('table.nb_non_treated_eggs')}</th>
                <th>{t('table.nb_birds')}</th>
              </tr>
            </thead>
            <tbody>
              {getScanData(key)}
              <tr key={`${key}-total`}>
                <td>{t('table.total')}</td>
                <td>{treatmentMissionSubjectsData['total'][key]['nests']}</td>
                <td>{treatmentMissionSubjectsData['total'][key]['nonTreatedNests']}</td>
                <td>{treatmentMissionSubjectsData['total'][key]['eggs']}</td>
                <td>{treatmentMissionSubjectsData['total'][key]['nonTreatedEggs']}</td>
                <td>{treatmentMissionSubjectsData['total'][key]['birds']}</td>
              </tr>
            </tbody>
          </table>
        </Div>
      )
    })
  }

  useEffect(() => {
    if (null === data) {
      get(`${import.meta.env.VITE_API_URL}/api/missions/${mission.id}/dashboard`, response => {
        const { errors, data } = response
        dispatch(fullUpdate({ errors, data }))
      })
    }
  }, [])

  return (
    <>
      {
        null !== data
          ?
          <Div cssClass='dashboard-global-vue columns is-multiline'>
            <Div cssClass='charts column is-12'>
              <Div cssClass='columns is-multiline'>
                {renderScanTreatmentTable()}
              </Div>
            </Div>
            <Div cssClass='charts column is-12'>
              <Div cssClass='columns is-multiline'>
                {renderScanDataByMissionSubject()}
              </Div>
            </Div>

            <Div cssClass='charts column is-12'>
              {renderCallsByDistrictBarChart()}
            </Div>
            <Div cssClass='charts column is-12'>
              {renderMonthlyCallsEvolutionLineChart()}
            </Div>
            <Div cssClass='charts column is-12'>
              <Div cssClass='columns'>
                <Div cssClass='column is-6'>
                  {renderCallsTypePieChart()}
                </Div>
                <Div cssClass='column is-6'>
                  {renderMissionSubjectsPieChart()}
                </Div>
              </Div>
            </Div>
            <Div cssClass='charts column is-12'>
              {renderWeeklyCallsEvolutionLineChart()}
            </Div>
          </Div>
          : <></>
      }
    </>
  )
}

export default Dashboard